import React, { FC } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { Button, Icons, Message, Paragraph } from '../../../../shared/components';
import { ErrorResponse } from '../../../../shared/hooks/useFetch';
import ExerciseFormContentFields from './ExerciseFormContentFields';
import StyledButtonGroup from './StyledButtonGroup';
import { ExerciseFormInitialValues, ExerciseFormMode, ExerciseFormType } from '../../../types';
import { BloomType, ExerciseType } from '../../../../../config/api/models/Exercise';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';

export type ExerciseFormContentProps = {
  mode?: ExerciseFormMode;
  type?: ExerciseFormType;
  onSubmit: (values: any) => void;
  onCancel?: () => void;
  error?: ErrorResponse;
  initialValues: ExerciseFormInitialValues;
};

export const defaultExerciseFormInitialValues = {
  title: '',
  description: '',
  level: '1',
  bloom_type: BloomType.REMEMBER,
  type: ExerciseType.H5P,
  algebra_kit_exercise_id: '',
  exercise_tool: '1',
};

const ExerciseFormContent: FC<ExerciseFormContentProps> = ({
  mode = 'normal',
  type = 'add',
  onSubmit,
  onCancel,
  error,
  initialValues,
}) => {
  const { t } = useTranslation();
  const loading = useAppSelector((state) => state.blocks.loading);

  return (
    <Form
      subscription={{
        submitting: true,
        values: true,
      }}
      onSubmit={onSubmit}
      initialValues={initialValues}
      vali
      render={({ handleSubmit, form, submitting, pristine, values }) => {
        const typeValue = form.getFieldState('type')
          ? form.getFieldState('type')?.value
          : values.type;
        const isAlgebraKitSelected = typeValue === ExerciseType.ALGEBRA_KIT;
        return (
          <form
            onSubmit={(...args) => {
              handleSubmit(...args);
              form.resetFieldState('title');
            }}
          >
            {
              {
                normal: (
                  <>
                    <ExerciseFormContentFields
                      mode={mode}
                      isAlgebraKitSelected={isAlgebraKitSelected}
                    />
                    <StyledButtonGroup>
                      <Button
                        type="submit"
                        icon={Icons.ADD_EXERCISES}
                        disabled={!form.getState().dirty}
                      >
                        {t('trajectory.sections.shared.addExerciseBtn')}
                      </Button>
                    </StyledButtonGroup>
                    {!!error && <Message type="error">{t(error.reason)}</Message>}
                  </>
                ),
                dialog: (
                  <>
                    <DialogTitle>
                      {
                        {
                          add: t('trajectory.page.blocks.h5p.addForm.title'),
                          update: t('trajectory.page.blocks.h5p.updateForm.title'),
                        }[type]
                      }
                    </DialogTitle>
                    <DialogContent>
                      <Paragraph>
                        {
                          {
                            add: t('trajectory.page.blocks.h5p.addForm.intro'),
                            update: t('trajectory.page.blocks.h5p.updateForm.intro'),
                          }[type]
                        }
                      </Paragraph>
                      <ExerciseFormContentFields
                        mode={mode}
                        type={type}
                        isAlgebraKitSelected={isAlgebraKitSelected}
                      />
                      {!!error && <Message type="error">{t(error.reason)}</Message>}
                    </DialogContent>
                    <DialogActions>
                      <Button variant="text" onClick={onCancel} disabled={loading}>
                        {t('shared.forms.cancel')}
                      </Button>
                      <Button type="submit" disabled={loading}>
                        {t('shared.forms.confirm')}
                      </Button>
                    </DialogActions>
                  </>
                ),
              }[mode]
            }
          </form>
        );
      }}
    />
  );
};

export default ExerciseFormContent;
